import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

export default function ReceiverCreated() {
  const { slug } = useParams();
  const location = useLocation();
  const { businessName, url, uploadUrl } = location.state || {};
  const [copied, setCopied] = useState(false);

  const fullUrl = uploadUrl || url || `https://files.happyhumantools.com/${slug}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Your File Receiving Page is Ready!
          </h1>
          <p className="text-xl text-gray-600">
            {businessName || 'Your page'} can now receive files instantly
          </p>
        </div>

        {/* Link Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Your unique file receiving link:
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={fullUrl}
                readOnly
                className="flex-1 px-4 py-3 text-lg bg-gray-50 border-2 border-gray-200 rounded-xl"
              />
              <button
                onClick={handleCopy}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
              >
                {copied ? '✓ Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all font-medium text-lg shadow-lg"
            >
              🚀 Visit Your Page
            </a>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-lg"
            >
              📁 Create Another
            </button>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            🎯 What happens next?
          </h2>

          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">Share your link</h3>
                <p className="text-gray-600">Send it to anyone who needs to send you files - clients, team members, friends</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">They upload files</h3>
                <p className="text-gray-600">Beautiful drag-and-drop interface works on any device</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">Files sync to your desktop</h3>
                <p className="text-gray-600">Install our desktop app to automatically download files to your computer</p>
                <a href="/download-desktop" className="inline-flex items-center mt-2 text-blue-600 hover:text-blue-700 font-medium">
                  Download Desktop App →
                </a>
              </div>
            </div>
          </div>

          {/* Pro Tips */}
          <div className="mt-8 p-6 bg-amber-50 rounded-2xl border-2 border-amber-200">
            <h3 className="font-semibold text-amber-900 mb-2 flex items-center">
              💡 Pro Tips
            </h3>
            <ul className="text-amber-800 space-y-1">
              <li>• Bookmark this page to manage your file receiver</li>
              <li>• Create folders for different projects or clients</li>
              <li>• Files are automatically organized by upload date</li>
            </ul>
          </div>
        </div>

        {/* Optional Email Capture */}
        <div className="mt-8 bg-white rounded-3xl shadow-xl p-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            📧 Want to manage your files better?
          </h3>
          <p className="text-gray-600 mb-4">
            Enter your email to access your dashboard and get notified when files arrive
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="your@email.com"
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
            />
            <button className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium">
              Get Access
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            No spam, ever. Just file notifications and your dashboard link.
          </p>
        </div>
      </div>
    </div>
  );
}